import React, { useState } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { PickupsResponseContentModel } from "../../../app/data/pickup/models";
import "./documentViewer.scss";
import pickup from "../../../images/bol_scan_1.svg";
import consignee from "../../../images/bol_scan_2.svg";
import phone from "../../../images/bol_scan_3.svg";
import shipper from "../../../images/bol_scan_4.svg";
import driver from "../../../images/bol_scan_5.svg";
import origin from "../../../images/bol_scan_6.svg";
import destination from "../../../images/bol_scan_7.svg";
import items from "../../../images/bol_scan_8.svg";
import weight from "../../../images/bol_scan_9.svg";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiQueryAdapter } from "../../../app/data/apiQueryAdapter";
import { toast } from "react-toastify";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/dist/plugins/zoom/index.js";
import Counter from "yet-another-react-lightbox/dist/plugins/counter/index.js";
import moment from "moment-timezone";
import dateFormats from "../../../app/data/common/dateFormats";

import "yet-another-react-lightbox/dist/styles.css";

// Helper function to convert GMT/UTC date string to local time and format
const formatDateFromGMT = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return moment.utc(dateString).local().format(dateFormats.uiDateTime);
};
export interface PickupDocumentViewerProps {
  pickup: PickupsResponseContentModel | null;
  isOpen: boolean;
  onClose: any;
}

export const PickupDocumentViewer: React.FC<PickupDocumentViewerProps> = (props) => {
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  const queryClient = useQueryClient();

  const approvePickupMutation = useMutation(
    async () => {
      return await apiQueryAdapter.query<null>(`/pickups/billing/${props.pickup?.pickup.pickupId}/approve`, "POST", {
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pickup_approvement']);
        toast.info("Pickup approved");
        props.onClose();
      },
      onError: (error) => {
        toast.error(error);
      },
    }
  );
  
  const handleApprovePickup = () => {
    approvePickupMutation.mutate();
  };

  const isAlreadyConfirmed = !!props.pickup?.billingApprovedDate;
  
  const documents = props.pickup?.pickupFullData.pickupRequest?.documents?.map((image, index) => ({
    src: image.storageUrl,
    alt: `Document ${index + 1}`,
    type: `Scan ${String(index + 1).padStart(2, '0')}`
  })) || [];

  const slides = documents.map((doc) => ({
    src: doc.src,
    alt: doc.alt
  }));

  return (
    <SlideOutSidebar show={props.isOpen} onClose={props.onClose} header={"BOL Pictures: " + props.pickup?.pickup.pickupNumber}>
      <div className="xgs-pickup-doc-viewer-info">
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={pickup} alt="Pickup #" />
          <span>Pickup #:</span>&nbsp;{props.pickup?.pickup.pickupNumber}
        </div>
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={consignee} alt="Consignee" />
          <span>Consignee:</span>&nbsp;{props.pickup?.consignee.name}
        </div>
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={phone} alt="Cons. Phone" />
          <span>Cons. Phone:</span>&nbsp;{props.pickup?.pickupFullData.pickupRequest.consigneePhone}
        </div>
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={shipper} alt="Shipper" />
          <span>Shipper:</span>&nbsp;{props.pickup?.shipper.name}
        </div>
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={driver} alt="Driver" />
          <span>Driver:</span>&nbsp;{props.pickup?.pickup.driverName}
        </div>
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={origin} alt="Origin SC" />
          <span>Origin SC:</span>&nbsp;{props.pickup?.originServiceCenter.airportCode} - {props.pickup?.originServiceCenter.terminalName} &nbsp; <span>{props.pickup?.originServiceCenter.terminalNumber}</span>
        </div>
        {!!props.pickup?.destinationServiceCenter && <div className="xgs-pickup-doc-viewer-info__item">
          <img src={destination} alt="Destination SC" />
          <span>Destination SC:</span>&nbsp;{props.pickup?.destinationServiceCenter?.airportCode} - {props.pickup?.destinationServiceCenter?.terminalName} &nbsp; <span>{props.pickup?.destinationServiceCenter?.terminalNumber}</span>
        </div>}
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={items} alt="Items" />
          <span>Items:</span>&nbsp;{props.pickup?.items.count}
        </div>
        <div className="xgs-pickup-doc-viewer-info__item">
          <img src={weight} alt="Weight" />
          <span>Weight:</span>&nbsp;{props.pickup?.items.weight} lbs.
        </div>
      </div>
      {!documents.length && <div className="xgs-pickup-doc-viewer__no-docs">No documents found</div>}
      {documents.length > 0 && (
        <div className="xgs-pickup-doc-viewer">
          <div className="xgs-pickup-doc-viewer__thumbnails">
            {documents.map((doc, idx) => (
              <div
                key={doc.src}
                className="xgs-pickup-doc-viewer__thumbnails__item"
                onClick={() => {
                  setLightboxIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <img src={doc.src} alt={doc.alt} />
                <p>{doc.type}</p>
              </div>
            ))}
          </div>
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            index={lightboxIndex}
            slides={slides}
            plugins={[
              Zoom as any,
              Counter as any
            ]}
            {...({
              zoom: {
                maxZoomPixelRatio: 2,
                scrollToZoom: true,
                doubleClickMaxStops: 1
              }
            } as any)}
            controller={{ closeOnBackdropClick: true }}
            styles={{
              container: { backgroundColor: "rgba(0, 0, 0, 0.85)" }
            }}
            on={{
              view: ({ index }) => setLightboxIndex(index),
            }}
          />
        </div>
      )}
      {isAlreadyConfirmed && (
        <div className="xgs-pickup-doc-viewer__already-confirmed">
          Already confirmed on {formatDateFromGMT(props.pickup?.billingApprovedDate)} by {props.pickup?.billingApprovedUser}
        </div>
      )}
      <div className="xgs-pickup-doc-viewer__buttons">
        <Button
          theme={ButtonThemes.blue}
          onClick={handleApprovePickup}
          spinner={approvePickupMutation.isLoading}
          disabled={isAlreadyConfirmed || approvePickupMutation.isLoading}
        >
          Confirm Probill
        </Button>
        <Button
          theme={ButtonThemes.gray}
          onClick={() => props.onClose()}
          disabled={approvePickupMutation.isLoading}
        >
          Cancel
        </Button>
      </div>
    </SlideOutSidebar>
  )
}
